import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React from "react";

import { HEADING_LENGTHS } from "@/shared/constants";
import { visitStoreWebsite } from "@/shared/helpers/tracking-helper";

import { truncateWithEllipsis } from "@/utils/strings";

import { ANALYTICS } from "@/analytics/constants";
import EVENTS from "@/analytics/events";
import { trackUserEvent } from "@/analytics/tracking";

import { useLanguageState } from "@/context/LanguageContext";

import { CategoryProps } from "@/interfaces/category-interface";
import { ThumbStoreProps } from "@/interfaces/stores-interface";

import { TileProps } from "@/types/tile";

import styles from "./retailerTiles.module.scss";

export type StoreTileProps = {
    store: ThumbStoreProps;
    category?: CategoryProps;
    parentCategory?: CategoryProps;
    analytics: ANALYTICS;
    featured?: boolean;
} & TileProps;

export const RetailerTile: React.FC<StoreTileProps> = (props) => {
    const { store, position, analytics } = props;
    const router = useRouter();
    const { localization } = useLanguageState();
    const { t } = useTranslation();
    const { merchantId, slug, name, mainImageUrl, newLogoUrl } = store;

    const eventProps = {
        slug,
        merchantName: name,
        merchantId: merchantId,
        categoryId: analytics.eventProps?.categoryId,
        httpCorrelationId: analytics.eventProps?.httpCorrelationId,
        position: position,
        query: analytics?.searchTerm,
        offerButton: false,
    };
    const onClick = () => {
        trackUserEvent(
            localization.locale,
            router.asPath,
            analytics.uiContext,
            EVENTS.SHOP_DIRECTORY_CLICK,
            eventProps
        );
        return visitStoreWebsite("stores/" + slug, false);
    };

    return (
        <a
            className={styles.storeTileContainer}
            role={"button"}
            onClick={onClick}
            href={`stores/${slug}`}
        >
            <img
                className={styles.storeTileImage}
                src={mainImageUrl}
                alt={t("retailerTile.mainImage", { merchantName: store.name })}
                loading={"lazy"}
            />
            <div className={styles.storeTileBottomContainer}>
                <img
                    className={styles.storeTileLogo}
                    src={newLogoUrl}
                    alt={t("retailerTile.logo", { merchantName: store.name })}
                    loading={"lazy"}
                />
                <p className={styles.storeTileName}>
                    {truncateWithEllipsis(name, HEADING_LENGTHS.TINY)}
                </p>
            </div>
        </a>
    );
};
